
import Vue from 'vue';
import { mapState } from 'vuex';
import Header from '@root/common/components/layout/header/Header.vue';
import HeaderBottom from '@root/common/components/layout/header/HeaderBottom.vue';
import HeaderPromotion from '@root/common/components/layout/header/HeaderPromotion.vue';
import Footer from '@root/common/components/layout/footer/Footer.vue';
import LiveStreamAnnouncement from '@root/common/components/layout/LiveStreamAnnouncement.vue';
import { Header as IHeader, Footer as IFooter, Mobile as IMobile } from '@root/modules/navigation/types/navigation';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import AdFormSiteheader from '@ads/components/adForm/AdFormSiteheader.vue';
import AdFormPromotionLine from '@ads/components/adForm/AdFormPromotionLine.vue';
import cookieBot from '@root/common/utils/cookieBot';
import getBannerKey from '@root/modules/ads/utils/getBannerKey';
import { formatAdSurround, resetFormatAdSurround } from '@root/modules/ads/utils/formatAdSurround';
import ScrollTop from '@root/common/components/base/ScrollTop.vue';
import isTildaArticle from '@root/modules/article/utils/isTildaArticle';
import isFullWidthArticle from '@root/modules/article/utils/isFullWidthArticle';
import AlertHandler from '@root/common/components/layout/AlertHandler.vue';
import { AlertComponentProps } from '@core/components/UI/alert/types';
import isMobileView from '@root/common/utils/isMobileView';

const PianoMaintenanceAlert = () => import('@piano/components/PianoMaintenanceAlert.vue');
const BigPlayer = () => import('@root/modules/podcast/components/player/BigPlayer.vue');
const DesktopPlayer = () => import('@root/modules/podcast/components/player/DesktopPlayer.vue');
const SmallPlayer = () => import('@root/modules/podcast/components/player/SmallPlayer.vue');

interface Computed {
  hideHeader: boolean;
  headerNavigation: IHeader;
  footerNavigation: IFooter;
  mobileNavigation: IMobile[];
  hideOrdering: boolean;
  alerts: AlertComponentProps[];
  adsPlacements: Record<string, any>;
  siteHeaderAd: string;
  mobileSiteHeaderAd: string;
  headerAd: Record<string, any>;
  floatingAd: Record<string, any>;
  scrollerAd: Record<string, any>;
  bottomAd: string;
  mobileinterstitialAd: string;
  promotionAd: Record<string, any>;
  mobilePromotionAd: Record<string, any>;
  mobileBottomAd: Record<string, any>;
  isDelfiFrontpage: boolean;
  isFrontpage: boolean;
  isArticle: boolean;
  isCustomPage: boolean;
  isCrArticle: boolean;
  isTildaArticle: boolean;
  isEasyChannel: boolean;
  isLiveBlogArticle: boolean;
  isFullWidthArticle: boolean;
  refetch: boolean;
  disableConsentModal: boolean;
  showPianoMaintenanceAlert: boolean;
  showPlayer: boolean;
  showBigPlayer: boolean;
  isPodcastChannel: boolean;
  isMultimedijaChannel: boolean;
  themeClass: string | null;
  adFreeClass: string | null;
}

interface Data {
  bannerKeys: Record<string, string>;
  isMobileView: boolean;
  observer: MutationObserver | null;
}

interface Methods {
  resetAdFormKey: (index?: number, skipHeader?: boolean) => void;
  copyUrlInject: () => void;
  getKey: (params: Record<string, any>) => string;
}

interface DefaultWindow extends Window {
  _AutofillCallbackHandler: any;
  dataLayer: any;
}

declare const window: DefaultWindow;

export default Vue.extend<Data, Methods, Computed, unknown>({
  components: {
    AdFormSiteheader,
    AdFormBanner,
    AdFormPromotionLine,
    Header,
    HeaderBottom,
    HeaderPromotion,
    Footer,
    LiveStreamAnnouncement,
    ScrollTop,
    PianoMaintenanceAlert,
    BigPlayer,
    DesktopPlayer,
    SmallPlayer,
    AlertHandler,
  },
  data() {
    return {
      bannerKeys: {
        siteHeader: 'siteheader',
        header: 'header',
        floating: 'floating',
        mobileBottom: 'mobileBottom',
        mobileInterstitial: 'mobileInterstitial',
        mobilePageview: 'mobilePageview',
        mobilePageviewGoogle: 'mobilePageviewGoogle',
        bottom: 'bottom',
        promotion: 'promotion',
      },
      isMobileView: false,
      observer: null,
    };
  },
  head() {
    const { id, lang, locale } = this.$channelConfig('settings');
    const { cbid } = this.$channelConfig('analytics')!.cookieBot;
    const bodyClass = this.hideHeader || this.isTildaArticle ? 'no-header' : '';
    const script = [];

    script.push({
      id: 'Cookiebot',
      src: 'https://consent.cookiebot.com/uc.js',
      'data-cbid': cbid,
      'data-framework': 'TCFv2.2',
      'data-culture': locale.split('_')[0],
      'data-wipe-preferences': '0',
      'data-wipe-statistics': '0',
      'data-wipe-marketing': '0',
    });

    script.push({
      hid: 'gemius',
      pbody: true,
      innerHTML: `
        window.pp_gemius_use_cmp = true;
        window.pp_gemius_cmp_timeout = 20000;
        window.pp_gemius_get_title = true;
        function gemius_pending(i) { window[i] = window[i] || function() {var x = window[i+'_pdata'] = window[i+'_pdata'] || []; x[x.length]=arguments;}; };
        gemius_pending('gemius_hit'); gemius_pending('gemius_event'); gemius_pending('pp_gemius_hit'); gemius_pending('pp_gemius_event');
        (function(d,t) { try { var gt=d.createElement(t),s=d.getElementsByTagName(t)[0],l='http'+((location.protocol=='https:')?'s':''); gt.setAttribute('async','async');
        gt.setAttribute('defer','defer'); gt.src=l+'://galt.hit.gemius.pl/xgemius.js'; s.parentNode.insertBefore(gt,s); } catch (e) {} })(document,'script');
      `,
    });

    // Inject ld+json into head
    const { base } = this.$channelConfig('meta');
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': id === 'fone' ? 'PodcastSeries' : 'WebSite',
      url: base.baseUrl,
      author: {
        '@type': 'Organization',
        name: 'Delfi',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Delfi',
      },
      description: base.description,
    };

    script.push({ hid: 'default-ld-json', type: 'application/ld+json', json: jsonLd });

    return {
      __dangerouslyDisableSanitizersByTagID: {
        gemius: ['innerHTML'],
      },
      htmlAttrs: {
        lang,
      },
      bodyAttrs: {
        class: bodyClass,
      },
      script,
    };
  },
  computed: {
    hideHeader() {
      const { specialFeatures } = this.$channelConfig('settings').application;
      return specialFeatures.disableHeader;
    },
    ...mapState({
      headerNavigation: (state: any) => state.navigation.header.data as IHeader,
      footerNavigation: (state: any) => state.navigation.footer.data as IFooter,
      mobileNavigation: (state: any) => state.navigation.mobile.data as IMobile[],
    }),
    hideOrdering() {
      const { specialFeatures } = this.$channelConfig('settings').application;

      return specialFeatures.hideSignupAndOrders.enabled;
    },
    alerts() {
      return this.$store.state.index.alerts || [];
    },
    adsPlacements() {
      const ads: Record<string, any> = this.$store.getters['adForm/getAdsPlacements'];

      return ads || {};
    },
    siteHeaderAd() {
      return (this.adsPlacements.siteheader && this.adsPlacements.siteheader.mid[0]) || null;
    },
    mobileSiteHeaderAd() {
      return (this.adsPlacements.mobilesiteheader && this.adsPlacements.mobilesiteheader.mid[0]) || null;
    },
    headerAd() {
      const placement: Record<string, any> = { mid: null, adb: null };

      if (!this.adsPlacements.header) {
        return placement;
      }

      if (this.isDelfiFrontpage && this.adsPlacements.header.dfp && this.adsPlacements.header.dfp.mid && this.adsPlacements.header.dfp.mid.length) {
        placement.mid = this.adsPlacements.header.dfp.mid[0];

        if (this.adsPlacements.header.dfp.group) {
          placement.group = this.adsPlacements.header.dfp.group;
        }
      } else if (this.adsPlacements.header.mid.length) {
        placement.mid = this.adsPlacements.header.mid[0];

        if (this.adsPlacements.header.group) {
          placement.group = this.adsPlacements.header.group;
        }
      }

      if (this.adsPlacements.header.adb) {
        placement.adb = this.adsPlacements.header.adb;
      }

      return placement;
    },
    floatingAd() {
      const placement: Record<string, any> = { mid: null, adb: null };

      if (!this.adsPlacements.floating) {
        return placement;
      }

      if (this.isDelfiFrontpage && this.adsPlacements.floating.dfp && this.adsPlacements.floating.dfp.mid && this.adsPlacements.floating.dfp.mid.length) {
        placement.mid = this.adsPlacements.floating.dfp.mid[0];
      } else if (this.adsPlacements.floating.mid.length) {
        placement.mid = this.adsPlacements.floating.mid[0];
      }

      if (this.adsPlacements.floating.adb) {
        placement.adb = this.adsPlacements.floating.adb;
      }

      return placement;
    },
    scrollerAd() {
      const placement: Record<string, any> = { mid: null, adb: null };

      if (this.adsPlacements.scroller && this.adsPlacements.scroller.mid.length > 0 && this.adsPlacements.scroller.mid[0]) {
        placement.mid = this.adsPlacements.scroller.mid[0];
      }

      if (this.adsPlacements.scroller && this.adsPlacements.scroller.adb) {
        placement.adb = this.adsPlacements.scroller.adb;
      }

      return placement;
    },
    bottomAd() {
      return (this.adsPlacements.bottom && this.adsPlacements.bottom.mid[0]) || null;
    },
    mobileinterstitialAd() {
      return (this.adsPlacements.mobileinterstitial && this.adsPlacements.mobileinterstitial.mid[0]) || null;
    },
    promotionAd() {
      return (this.adsPlacements.promotion && this.adsPlacements.promotion.mid[0]) || null;
    },
    mobilePromotionAd() {
      return (this.adsPlacements.mobilepromotion && this.adsPlacements.mobilepromotion.mid[0]) || null;
    },
    mobileBottomAd() {
      return (this.adsPlacements.mobilebottom && this.adsPlacements.mobilebottom.mid[0]) || null;
    },
    isDelfiFrontpage() {
      return this.isFrontpage && this.$channelConfig('settings').id === 'delfi';
    },
    isFrontpage() {
      return this.$route.name === 'frontpage';
    },
    isArticle() {
      return this.$route.name === 'article';
    },
    isCustomPage() {
      return typeof this.$route.name === 'string' && this.$route.name.startsWith('customPage');
    },
    isCrArticle() {
      if (!this.isArticle) {
        return false;
      }

      const activeArticle = this.$store.getters['article/getActiveArticle'];
      if (!activeArticle) {
        return false;
      }

      const crArticles = this.$store.getters['article/getCrArticles'];
      const isCrArticle = crArticles.find((article: number) => article === activeArticle.id);

      return this.isArticle && isCrArticle;
    },
    isTildaArticle() {
      if (!this.isArticle) {
        return false;
      }

      const customCategorySettings = this.$channelConfig('page').category?.component?.header;
      return isTildaArticle(this.$route, customCategorySettings);
    },
    isEasyChannel() {
      const { id } = this.$channelConfig('settings');
      return id === 'zinios-visiems';
    },
    isLiveBlogArticle() {
      if (!this.isArticle) {
        return false;
      }

      const activeArticle = this.$store.getters['article/getActiveArticle'];
      if (!activeArticle) {
        return false;
      }
      return activeArticle && activeArticle.settings?.articleTemplate && activeArticle.settings?.articleTemplate === 'liveBlog';
    },
    isFullWidthArticle() {
      if (!this.isArticle) {
        return false;
      }

      const activeArticle = this.$store.getters['article/getActiveArticle'];
      if (!activeArticle) {
        return false;
      }

      return isFullWidthArticle(activeArticle);
    },
    refetch() {
      return this.$store.state.frontpage.refetch;
    },
    disableConsentModal() {
      const { specialFeatures } = this.$channelConfig('settings').application;

      return specialFeatures.disableConsentModal;
    },
    showPianoMaintenanceAlert() {
      return this.$store.state.piano.maintenanceAlertVisible;
    },
    showPlayer() {
      const isPlaying = Boolean(this.$player?.state.currentlyPlaying.audioId);
      const lastPlayed = this.$player?.state.lastPlayed;

      // Setup player to resume playing
      if (process.client && !isPlaying && lastPlayed !== null) {
        this.$player?.setup(lastPlayed, true);
      }

      return isPlaying || (!isPlaying && lastPlayed !== null);
    },
    showBigPlayer() {
      return this.$player?.state.showBigPlayer;
    },
    isPodcastChannel() {
      return this.$channelConfig('settings').id === 'fone';
    },
    isMultimedijaChannel() {
      return this.$channelConfig('settings').id === 'multimedija' || this.$channelConfig('settings').id === 'multimedia';
    },
    themeClass() {
      return this.$channelConfig('settings').theme !== 'delfi' ? `theme-${this.$channelConfig('settings').theme}` : null;
    },
    adFreeClass() {
      return this.$store.getters['adForm/getIsAdFree'] ? 'isadf' : null;
    },
  },
  watch: {
    // asyncData logic - key will change after page asyncData has finished
    $route() {
      if (process.client) {
        this.resetAdFormKey();
      }

      resetFormatAdSurround();

      if (process.client) {
        // Dispatch custom event for player.js
        window.dispatchEvent(new CustomEvent('nuxtRouteChange'));
      }
    },
    refetch(refetch) {
      if (refetch) {
        this.resetAdFormKey(Math.floor(10000 + Math.random() * 90000));
      }
    },
    showBigPlayer(isOpen) {
      if (isOpen) {
        document.body.classList.add('no-scroll');
      } else {
        document.body.classList.remove('no-scroll');
      }
    },
  },
  mounted() {
    this.isMobileView = isMobileView();

    // Cookiebot onLoad hook
    cookieBot();

    // Remove self-injected Google CMP container in review
    if (this.disableConsentModal || this.hideOrdering) {
      document.body.classList.add('no-user');

      const mutationCallback = (mutations: any) => {
        const mutation = mutations[0] as MutationRecord;
        const nodes = mutation.addedNodes as NodeList;
        const element = nodes[0] as HTMLElement;

        if (element && element.nodeName === 'DIV' && element.className === 'fc-consent-root') {
          element.remove();
        }
      };

      this.observer = new MutationObserver(mutationCallback);
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    // deepcode ignore InsufficientPostmessageValidation: <validation is made in handler>
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.data === 'formatAdSurround') {
        formatAdSurround();
      }
    });

    // Mute unknown FB iOS app error
    // https://developers.facebook.com/community/threads/320013549791141
    window._AutofillCallbackHandler = window._AutofillCallbackHandler || function () {};

    try {
      this.$instanceConnector('headlines').listen('headlineClick', async ({ headline }) => {
        if (headline.clickData) {
          this.$event.send('link-click', { url: headline.href, ...headline.clickData });
        }
      });
    } catch (e) {
      this.$sentry.captureException(`Headline click fail: ${e}`, {
        tags: { 'process.server': process.server ? 'server' : 'client' },
        contexts: { vue: { componentName: 'default.vue layout' } },
      });
    }

    this.copyUrlInject();
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    getKey({ prefix, index }) {
      return getBannerKey({ prefix, route: this.$route, store: this.$store, index });
    },
    resetAdFormKey(index = 0) {
      this.bannerKeys = {
        siteHeader: this.getKey({ prefix: 'siteheader', index }),
        header: this.getKey({ prefix: 'header', index }),
        floating: this.getKey({ prefix: 'floating', index }),
        mobileBottom: this.getKey({ prefix: 'mobileBottom', index }),
        mobileBottomAlt: this.getKey({ prefix: 'mobileBottomAlt', index }),
        mobileInterstitial: this.getKey({ prefix: 'mobileInterstitial', index }),
        bottom: this.getKey({ prefix: 'bottom', index }),
      };
    },
    copyUrlInject() {
      if (!process.client || !window) {
        return;
      }

      window.document.addEventListener('copy', () => {
        const sel = window.getSelection();

        if (!sel || sel.anchorNode?.nodeName !== '#text' || sel.toString().length < 32) {
          return;
        }

        const selectionNode = sel.rangeCount > 0 ? sel.getRangeAt(0).commonAncestorContainer : sel.anchorNode;
        const selectionParent = selectionNode?.nodeType === Node.TEXT_NODE ? selectionNode.parentElement : selectionNode;
        const articleElement = selectionParent instanceof Element ? selectionParent.closest('.article') : null;
        const isPaid = articleElement?.getAttribute('data-paywall-enabled') === 'true';

        if (sel.toString().length > 100 && isPaid) {
          sel.removeAllRanges();
        }

        const loc = this.$t('copy_inject_url.message');
        const xtext = '<br/><br/> ' + loc + '<a href="' + document.location.href + '">' + document.location.href + '</a><br/><br/>';

        const div = window.document.createElement('div');
        // deepcode ignore DOMXSS: <data is not user input>
        div.innerHTML = sel + xtext;
        div.style.position = 'absolute';
        div.style.left = '-99999px';

        // deepcode ignore DOMXSS: <data is not user input>
        document.body.appendChild(div);

        sel.selectAllChildren(div);

        window.setTimeout(function () {
          div.remove();
        }, 0);
      });
    },
  },
});
